/**
 * EOS Rocks - Horizontal Slider by Person (Slack-style)
 * 
 * Features:
 * - Horizontal scrollable columns by owner
 * - Card-based display with status indicators
 * - Modal-based add/edit (no side panels)
 * - Inline "+ Add Rock" per person column
 */

import React, { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Target, 
  Plus, 
  Edit, 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Circle,
  MoreHorizontal,
  Trash2,
  ListTodo,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UserAvatar from '../components/UserAvatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  not_started: { label: 'Not Started', color: 'bg-slate-400', icon: Circle, textColor: 'text-slate-400' },
  on_track: { label: 'On Track', color: 'bg-green-500', icon: CheckCircle2, textColor: 'text-green-500' },
  at_risk: { label: 'At Risk', color: 'bg-yellow-500', icon: AlertTriangle, textColor: 'text-yellow-500' },
  off_track: { label: 'Off Track', color: 'bg-red-500', icon: XCircle, textColor: 'text-red-500' },
  complete: { label: 'Complete', color: 'bg-blue-500', icon: CheckCircle2, textColor: 'text-blue-500' },
};

// Rock Card Component
const RockCard = ({ rock, onClick, onStatusChange }) => {
  const status = STATUS_CONFIG[rock.status] || STATUS_CONFIG.not_started;
  const StatusIcon = status.icon;
  const daysRemaining = rock.quarter_end_date 
    ? Math.max(0, Math.ceil((new Date(rock.quarter_end_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;
  
  return (
    <Card 
      className="dark:bg-slate-700/50 border-slate-600 hover:border-blue-400 cursor-pointer transition-all hover:shadow-lg group"
      onClick={() => onClick(rock)}
    >
      <CardContent className="p-4">
        {/* Status Badge */}
        <div className="flex items-center justify-between mb-2">
          <Badge 
            variant="outline" 
            className={`${status.color} text-white border-0 text-xs`}
          >
            <StatusIcon className="h-3 w-3 mr-1" />
            {status.label}
          </Badge>
          {rock.rock_type && (
            <span className="text-xs text-slate-400">
              {rock.rock_type === 'company' ? '🏢' : rock.rock_type === 'department' ? '🏬' : '👤'}
            </span>
          )}
        </div>
        
        {/* Title */}
        <h4 className="font-semibold text-sm dark:text-white mb-2 line-clamp-2">
          {rock.title}
        </h4>
        
        {/* Progress Bar */}
        <div className="mb-2">
          <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
            <span>Progress</span>
            <span>{rock.progress_percentage || 0}%</span>
          </div>
          <Progress value={rock.progress_percentage || 0} className="h-1.5" />
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-slate-400">
          {rock.quarter && <span>{rock.quarter}</span>}
          {daysRemaining !== null && (
            <span className={daysRemaining <= 14 ? 'text-yellow-400' : ''}>
              {daysRemaining}d left
            </span>
          )}
        </div>
        
        {/* Subtasks indicator */}
        {rock.milestones && rock.milestones.length > 0 && (
          <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
            <ListTodo className="h-3 w-3" />
            <span>
              {rock.milestones.filter(m => m.completed).length}/{rock.milestones.length} milestones
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Person Column Component
const PersonColumn = ({ user, rocks, onAddRock, onRockClick }) => {
  const userRocks = rocks.filter(r => r.owner_id === user.id);
  const completedCount = userRocks.filter(r => r.status === 'complete').length;
  
  return (
    <div className="flex-shrink-0 w-72 bg-slate-800/40 rounded-lg border border-slate-700">
      {/* Column Header */}
      <div className="p-3 border-b border-slate-700 sticky top-0 bg-slate-800/90 backdrop-blur-sm rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserAvatar user={user} size="sm" />
            <div>
              <p className="font-medium text-sm dark:text-white">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-slate-400">
                {userRocks.length} rock{userRocks.length !== 1 ? 's' : ''} • {completedCount} done
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Rocks List */}
      <div className="p-2 space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto scrollbar-thin">
        {userRocks.map(rock => (
          <RockCard 
            key={rock.id} 
            rock={rock} 
            onClick={onRockClick}
          />
        ))}
        
        {userRocks.length === 0 && (
          <p className="text-center text-slate-500 text-sm py-4">No rocks assigned</p>
        )}
        
        {/* Add Rock Button */}
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700/50 border border-dashed border-slate-600"
          onClick={() => onAddRock(user)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Rock
        </Button>
      </div>
    </div>
  );
};

const EOSRocks = () => {
  const [rocks, setRocks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuarter, setSelectedQuarter] = useState('Q1 2026');
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
  const scrollContainerRef = useRef(null);
  
  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['super_admin', 'admin', 'ceo', 'president'].includes(currentUser.role);
  
  // Modal States
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRock, setSelectedRock] = useState(null);
  const [prefilledOwner, setPrefilledOwner] = useState(null);
  
  // Form State
  const [rockForm, setRockForm] = useState({
    title: '',
    description: '',
    success_criteria: '',
    owner_id: '',
    rock_type: 'personal',
    department: '',
    quarter: 'Q1 2026',
    quarter_start_date: '2026-01-01',
    quarter_end_date: '2026-03-31',
    milestones: []
  });
  
  // Edit Form State
  const [editForm, setEditForm] = useState({});
  const [newMilestone, setNewMilestone] = useState('');

  useEffect(() => {
    fetchRocks();
    fetchUsers();
  }, [selectedQuarter]);

  const fetchRocks = async () => {
    try {
      const params = { quarter: selectedQuarter };
      const response = await axios.get(`${API}/eos/rocks`, { params });
      setRocks(response.data);
    } catch (error) {
      console.error('Error fetching rocks:', error);
      toast.error('Failed to load rocks');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      // Show all active users for rock assignment
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleAddRock = (user) => {
    setPrefilledOwner(user);
    setRockForm(prev => ({
      ...prev,
      owner_id: user.id,
      quarter: selectedQuarter
    }));
    setCreateDialogOpen(true);
  };

  const handleCreateRock = async () => {
    try {
      if (!rockForm.title || !rockForm.owner_id) {
        toast.error('Please fill in title and owner');
        return;
      }

      await axios.post(`${API}/eos/rocks`, rockForm);
      toast.success('Rock created successfully');
      setCreateDialogOpen(false);
      resetRockForm();
      fetchRocks();
    } catch (error) {
      console.error('Error creating rock:', error);
      toast.error(error.response?.data?.detail || 'Failed to create rock');
    }
  };

  const handleRockClick = (rock) => {
    setSelectedRock(rock);
    setEditForm({
      ...rock,
      milestones: rock.milestones || []
    });
    setEditDialogOpen(true);
  };

  const handleUpdateRock = async () => {
    try {
      await axios.put(`${API}/eos/rocks/${selectedRock.id}`, editForm);
      toast.success('Rock updated successfully');
      setEditDialogOpen(false);
      fetchRocks();
    } catch (error) {
      console.error('Error updating rock:', error);
      toast.error('Failed to update rock');
    }
  };

  const handleUpdateProgress = async (newProgress) => {
    try {
      await axios.patch(`${API}/eos/rocks/${selectedRock.id}/progress`, {
        progress_percentage: newProgress,
        status: newProgress >= 100 ? 'complete' : editForm.status,
        notes: ''
      });
      setEditForm(prev => ({ ...prev, progress_percentage: newProgress }));
      fetchRocks();
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const handleDeleteRock = async () => {
    try {
      await axios.delete(`${API}/eos/rocks/${selectedRock.id}`);
      toast.success('Rock deleted');
      setDeleteDialogOpen(false);
      setEditDialogOpen(false);
      setSelectedRock(null);
      fetchRocks();
    } catch (error) {
      console.error('Error deleting rock:', error);
      toast.error('Failed to delete rock');
    }
  };

  const handleAddMilestone = () => {
    if (!newMilestone.trim()) return;
    setEditForm(prev => ({
      ...prev,
      milestones: [...(prev.milestones || []), { title: newMilestone, completed: false }]
    }));
    setNewMilestone('');
  };

  const handleToggleMilestone = (index) => {
    setEditForm(prev => ({
      ...prev,
      milestones: prev.milestones.map((m, i) => 
        i === index ? { ...m, completed: !m.completed } : m
      )
    }));
  };

  const handleRemoveMilestone = (index) => {
    setEditForm(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index)
    }));
  };

  const resetRockForm = () => {
    setRockForm({
      title: '',
      description: '',
      success_criteria: '',
      owner_id: '',
      rock_type: 'personal',
      department: '',
      quarter: selectedQuarter,
      quarter_start_date: '2026-01-01',
      quarter_end_date: '2026-03-31',
      milestones: []
    });
    setPrefilledOwner(null);
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // Calculate stats
  const stats = {
    total: rocks.length,
    complete: rocks.filter(r => r.status === 'complete').length,
    onTrack: rocks.filter(r => r.status === 'on_track').length,
    atRisk: rocks.filter(r => r.status === 'at_risk').length,
    offTrack: rocks.filter(r => r.status === 'off_track').length,
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Rocks...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="h-8 w-8 text-purple-500" />
            <div>
              <h1 className="text-2xl font-bold dark:text-white">90-Day Rocks</h1>
              <p className="text-sm text-muted-foreground">
                Track quarterly objectives by team member
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Quarter Selector */}
            <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
              <SelectTrigger className="w-32 dark:bg-slate-700 dark:border-slate-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                <SelectItem value="Q1 2025">Q1 2025</SelectItem>
                <SelectItem value="Q2 2025">Q2 2025</SelectItem>
                <SelectItem value="Q3 2025">Q3 2025</SelectItem>
                <SelectItem value="Q4 2025">Q4 2025</SelectItem>
                <SelectItem value="Q1 2026">Q1 2026</SelectItem>
              </SelectContent>
            </Select>
            
            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-slate-700 overflow-hidden">
              <button
                onClick={() => setViewMode('my')}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  viewMode === 'my' 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                My Rocks
              </button>
              {isAdmin && (
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    viewMode === 'all' 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-slate-800 text-slate-400 hover:text-white'
                  }`}
                >
                  All Rocks
                </button>
              )}
            </div>
            
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Rock
            </Button>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="flex gap-4 p-3 bg-slate-800/40 rounded-lg border border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-slate-400 text-sm">Total:</span>
            <Badge variant="outline" className="dark:bg-slate-700">{stats.total}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-green-400 text-sm">On Track:</span>
            <Badge className="bg-green-500">{stats.onTrack}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-yellow-400 text-sm">At Risk:</span>
            <Badge className="bg-yellow-500">{stats.atRisk}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-red-400 text-sm">Off Track:</span>
            <Badge className="bg-red-500">{stats.offTrack}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-blue-400 text-sm">Complete:</span>
            <Badge className="bg-blue-500">{stats.complete}</Badge>
          </div>
        </div>

        {/* Horizontal Slider Container */}
        <div className="relative">
          {/* Scroll Buttons */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
            onClick={scrollLeft}
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-slate-800/80 hover:bg-slate-700 rounded-full shadow-lg"
            onClick={scrollRight}
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
          
          {/* Scrollable Container */}
          <div 
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto px-10 pb-4 scrollbar-thin"
            style={{ scrollbarWidth: 'thin' }}
          >
            {(viewMode === 'my' 
              ? users.filter(u => u.id === currentUser.user_id || u.id === currentUser.id)
              : users
            ).map(user => (
              <PersonColumn
                key={user.id}
                user={user}
                rocks={rocks}
                onAddRock={handleAddRock}
                onRockClick={handleRockClick}
              />
            ))}
            
            {users.length === 0 && (
              <div className="flex-1 flex items-center justify-center p-8 text-slate-500">
                No team members found. Add users to start tracking rocks.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Rock Modal */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-500" />
              Create New Rock
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {prefilledOwner && (
                <span>Adding rock for {prefilledOwner.first_name} {prefilledOwner.last_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="dark:text-slate-200">Title *</Label>
              <Input
                value={rockForm.title}
                onChange={(e) => setRockForm({ ...rockForm, title: e.target.value })}
                placeholder="e.g., Increase Q1 revenue by 20%"
                className="dark:bg-slate-700 dark:border-slate-600"
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Owner *</Label>
              <Select 
                value={rockForm.owner_id} 
                onValueChange={(val) => setRockForm({ ...rockForm, owner_id: val })}
              >
                <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                  <SelectValue placeholder="Select owner" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="dark:text-slate-200">Type</Label>
                <Select 
                  value={rockForm.rock_type} 
                  onValueChange={(val) => setRockForm({ ...rockForm, rock_type: val })}
                >
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="company">🏢 Company</SelectItem>
                    <SelectItem value="department">🏬 Department</SelectItem>
                    <SelectItem value="personal">👤 Personal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="dark:text-slate-200">Quarter</Label>
                <Select 
                  value={rockForm.quarter} 
                  onValueChange={(val) => setRockForm({ ...rockForm, quarter: val })}
                >
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="Q1 2025">Q1 2025</SelectItem>
                    <SelectItem value="Q2 2025">Q2 2025</SelectItem>
                    <SelectItem value="Q3 2025">Q3 2025</SelectItem>
                    <SelectItem value="Q4 2025">Q4 2025</SelectItem>
                    <SelectItem value="Q1 2026">Q1 2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Success Criteria</Label>
              <Textarea
                value={rockForm.success_criteria}
                onChange={(e) => setRockForm({ ...rockForm, success_criteria: e.target.value })}
                placeholder="How will you know this rock is complete?"
                className="dark:bg-slate-700 dark:border-slate-600"
                rows={2}
              />
            </div>
            
            <div>
              <Label className="dark:text-slate-200">Description (Optional)</Label>
              <Textarea
                value={rockForm.description}
                onChange={(e) => setRockForm({ ...rockForm, description: e.target.value })}
                placeholder="Additional context..."
                className="dark:bg-slate-700 dark:border-slate-600"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetRockForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateRock}>
              Create Rock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Rock Modal */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="dark:text-white flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-500" />
              Edit Rock
            </DialogTitle>
          </DialogHeader>
          
          {selectedRock && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="dark:text-slate-200">Title</Label>
                <Input
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="dark:bg-slate-700 dark:border-slate-600"
                />
              </div>
              
              <div>
                <Label className="dark:text-slate-200">Status</Label>
                <Select 
                  value={editForm.status || 'not_started'} 
                  onValueChange={(val) => setEditForm({ ...editForm, status: val })}
                >
                  <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="not_started">⚪ Not Started</SelectItem>
                    <SelectItem value="on_track">🟢 On Track</SelectItem>
                    <SelectItem value="at_risk">🟡 At Risk</SelectItem>
                    <SelectItem value="off_track">🔴 Off Track</SelectItem>
                    <SelectItem value="complete">✅ Complete</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="dark:text-slate-200">Progress ({editForm.progress_percentage || 0}%)</Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    type="range"
                    min="0"
                    max="100"
                    value={editForm.progress_percentage || 0}
                    onChange={(e) => handleUpdateProgress(parseInt(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium w-12 text-right dark:text-white">
                    {editForm.progress_percentage || 0}%
                  </span>
                </div>
                <Progress value={editForm.progress_percentage || 0} className="h-2 mt-2" />
              </div>
              
              <div>
                <Label className="dark:text-slate-200">Success Criteria</Label>
                <Textarea
                  value={editForm.success_criteria || ''}
                  onChange={(e) => setEditForm({ ...editForm, success_criteria: e.target.value })}
                  className="dark:bg-slate-700 dark:border-slate-600"
                  rows={2}
                />
              </div>
              
              {/* Milestones / Subtasks */}
              <div>
                <Label className="dark:text-slate-200">Milestones</Label>
                <div className="space-y-2 mt-2">
                  {(editForm.milestones || []).map((milestone, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-700/50 rounded">
                      <input
                        type="checkbox"
                        checked={milestone.completed}
                        onChange={() => handleToggleMilestone(idx)}
                        className="rounded"
                      />
                      <span className={`flex-1 text-sm ${milestone.completed ? 'line-through text-slate-500' : 'dark:text-white'}`}>
                        {milestone.title}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMilestone(idx)}
                        className="h-6 w-6 p-0 text-slate-400 hover:text-red-400"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  
                  <div className="flex gap-2">
                    <Input
                      value={newMilestone}
                      onChange={(e) => setNewMilestone(e.target.value)}
                      placeholder="Add milestone..."
                      className="dark:bg-slate-700 dark:border-slate-600"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddMilestone()}
                    />
                    <Button size="sm" onClick={handleAddMilestone}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div>
                <Label className="dark:text-slate-200">Notes</Label>
                <Textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  placeholder="Additional notes..."
                  className="dark:bg-slate-700 dark:border-slate-600"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="flex justify-between">
            <Button 
              variant="destructive" 
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRock}>
                Save Changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">Delete Rock?</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-slate-400">
              This action cannot be undone. This will permanently delete the rock
              &ldquo;{selectedRock?.title}&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="dark:bg-slate-700 dark:border-slate-600">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteRock}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default EOSRocks;
